import { EIntegracoesAPIEnum } from "../../../../../@types/IntegracoesAPIEnum";
import {
  API_BY_HASH_NAMES,
  TAPIRules
} from "../../../../../@types/IntegracoesAPIRules";
import { TChamarServicoParams } from "../../../services/PainelDeIntegracaoesGateway";

type TPluggyPayload = {
  serviceId: string;
  start: string;
  end?: string;
};

type TDateLikeValue = string | Date | null | undefined;

const pickFirstValue = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return undefined;
};

const DEFAULT_BUSCAR_NOVAMENTE_RULE = {
  actionByDate: "/v1/WebserviceAPI/CallWebserviceByDate",
  actionByRange: "/v1/WebserviceAPI/CallWebserviceByRangeDate",
  companyEditable: false
} as const;

const CENTRALIZED_INTEGRATIONS_RANGE_URL =
  "/v1/WebserviceAPI/CallSelectedIntegrationsByRangeDate";

const buildDefaultApiRules = (
  apiHash: number,
  apiName?: string
): TAPIRules | undefined => {
  const normalizedApiName = apiName?.trim();

  if (!normalizedApiName) return undefined;

  return {
    ...DEFAULT_BUSCAR_NOVAMENTE_RULE,
    apiHash: apiHash as EIntegracoesAPIEnum,
    api: normalizedApiName
  };
};

export const parseLocalDate = (value: string | Date): Date => {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const localDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());

  if (!localDateMatch) {
    return new Date(value);
  }

  const [, year, month, day] = localDateMatch;

  return new Date(Number(year), Number(month) - 1, Number(day));
};

const pickFirstDateValue = (
  ...values: TDateLikeValue[]
): string | Date | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }
  }

  return undefined;
};

export const serializeLegacyDate = (
  value?: TDateLikeValue
): string | undefined => {
  const normalizedValue = pickFirstDateValue(value);

  if (!normalizedValue) return undefined;

  if (normalizedValue instanceof Date) {
    return normalizedValue.toISOString();
  }

  const parsedDate = parseLocalDate(normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toISOString();
};

const serializeMetadata = (
  metadata?: string | string[] | null,
  preserveEmptySelection = false
): string | undefined => {
  if (Array.isArray(metadata)) {
    const selectedValues = metadata.filter(
      (value) => typeof value === "string" && value.trim().length > 0
    );

    return selectedValues.length > 0
      ? JSON.stringify(selectedValues)
      : preserveEmptySelection
        ? "[]"
        : undefined;
  }

  return pickFirstValue(metadata ?? undefined) ?? (preserveEmptySelection ? "[]" : undefined);
};

export const resolveApiRules = (
  apiHash: string | number | undefined,
  apiName?: string
): TAPIRules | undefined => {
  const resolveByName = (): TAPIRules | undefined => {
    if (typeof apiName !== "string" || apiName.trim().length === 0) {
      return undefined;
    }

    // TODO[FINC-1618]: remover fallback por nome legado.
    const normalizedApiName = apiName.trim().toLocaleLowerCase();

    return Object.values(API_BY_HASH_NAMES).find(
      (rule) => rule?.api?.trim().toLocaleLowerCase() === normalizedApiName
    );
  };

  if (apiHash == null || apiHash === "") return resolveByName();

  const parsedApiHash = Number(apiHash);

  if (!Number.isInteger(parsedApiHash)) return resolveByName();

  return (
    API_BY_HASH_NAMES[parsedApiHash as EIntegracoesAPIEnum] ??
    resolveByName() ??
    buildDefaultApiRules(parsedApiHash, apiName)
  );
};

export const shouldUseRange = (
  params: Pick<TChamarServicoParams, "selecionarPeriodo">,
  apiRules?: TAPIRules
): boolean => {
  if (!apiRules) return params.selecionarPeriodo === true;

  return (
    apiRules.forcesRange === true ||
    params.selecionarPeriodo === true ||
    (!apiRules.actionByDate && !!apiRules.actionByRange)
  );
};

export const resolveChamarServicoUrl = (
  params: Pick<TChamarServicoParams, "selecionarPeriodo" | "integrations">,
  apiRules?: TAPIRules
): string | undefined => {
  if (params.integrations?.length && shouldUseRange(params, apiRules)) {
    return CENTRALIZED_INTEGRATIONS_RANGE_URL;
  }

  if (!apiRules) return undefined;

  if (!shouldUseRange(params, apiRules)) {
    return apiRules.actionByDate;
  }

  return apiRules.actionByRange;
};

export const supportsSubmitChamarServico = (
  apiRules?: TAPIRules
): boolean => {
  // TODO[FINC-1618]: mover suporte do fluxo para contrato do backend.
  if (!apiRules) return false;

  if (apiRules.drawerMode === "informative") return false;

  return Boolean(
    apiRules.usesCustomChamarServicoGateway ||
      apiRules.actionByDate ||
      apiRules.actionByRange
  );
};

export const supportsOpenBuscarNovamente = (
  apiRules?: TAPIRules
): boolean => {
  // TODO[FINC-1618]: mover abertura do drawer para contrato do backend.
  if (!apiRules) return false;

  return (
    apiRules.drawerMode === "informative" || supportsSubmitChamarServico(apiRules)
  );
};

export const supportsChamarServico = supportsSubmitChamarServico;

export const normalizeGatewayUrl = (url: string): string => {
  const sanitizedUrl = url.trim().replace(/^\/+/, "");

  return `/${sanitizedUrl}`;
};

export const normalizePluggyPayload = (
  params: TChamarServicoParams
): TPluggyPayload => {
  // Regra de negócio do front-end legado em Global.js: Pluggy recebe as
  // datas como start/end, com formatos diferentes do formulário Vue.
  const byRange = params.PluggyByRange === true;
  const start = pickFirstDateValue(
    byRange ? params.DataInicio : params.start,
    byRange ? params.inicio ?? undefined : params.date,
    byRange ? undefined : params.Data
  );
  const end = byRange
    ? pickFirstDateValue(params.DataFim, params.fim ?? undefined, params.end)
    : undefined;

  return {
    serviceId: params.serviceId ?? "",
    start: serializeLegacyDate(start) ?? "",
    end: serializeLegacyDate(end)
  };
};

export const normalizeChamarServicoPayload = (
  params: TChamarServicoParams,
  apiRules?: TAPIRules
): TChamarServicoParams => {
  // TODO[FINC-1618]: remover tradução manual de payload do front.
  const isCentralizedRangeRequest =
    resolveChamarServicoUrl(params, apiRules) ===
    CENTRALIZED_INTEGRATIONS_RANGE_URL;
  const normalizedParams: TChamarServicoParams = isCentralizedRangeRequest
    ? { integrations: params.integrations ?? [] }
    : { serviceId: params.serviceId };
  const useRange = shouldUseRange(params, apiRules);
  const shouldIgnoreDisabledCompanyFieldValue =
    apiRules?.companyEditable === false && apiRules?.requiresCompanyLookup !== true;
  const shouldPreserveEmptyMetadataSelection =
    (apiRules?.metadataOptions?.length ?? 0) > 0;

  // TODO[FINC-1618]: mover regra de envio de empresa para contrato do backend.
  const empresaId = shouldIgnoreDisabledCompanyFieldValue
    ? pickFirstValue(params.empresaId)
    : pickFirstValue(params.empresaId, params.empresa);
  const date = pickFirstDateValue(params.date, params.data, params.Data);
  const metadata = serializeMetadata(
    params.metadata,
    shouldPreserveEmptyMetadataSelection
  );

  if (empresaId) {
    normalizedParams.empresaId = empresaId;
  }

  if (metadata) {
    normalizedParams.metadata = metadata;
  }

  if (useRange) {
    normalizedParams.inicio =
      serializeLegacyDate(
        pickFirstDateValue(params.inicio, params.DataInicio) ?? null
      ) ?? null;
    normalizedParams.fim =
      serializeLegacyDate(
        pickFirstDateValue(params.fim, params.DataFim) ?? null
      ) ?? null;
  } else if (date) {
    normalizedParams.date = serializeLegacyDate(date) ?? date;
  }

  switch (params.apiHash) {
    case EIntegracoesAPIEnum.NFe:
      normalizedParams.empresaId =
        pickFirstValue(params.empresaId, params.empresa) ?? "";
      normalizedParams.origemSimplificada =
        pickFirstValue(params.origemSimplificada) ?? "1";
      break;
    case EIntegracoesAPIEnum.CADIS:
      normalizedParams.tipoIntegracao =
        pickFirstValue(params.tipoDeIntegracao, params.tipoDePesquisa) ?? "0";
      break;
    case EIntegracoesAPIEnum.MoozCartoes:
      normalizedParams.tipoDeArquivo =
        pickFirstValue(params.tipoDeArquivo, params.tipoArquivo) ?? "0";
      break;
    case EIntegracoesAPIEnum.PDVDataSystem:
      normalizedParams.metadata =
        pickFirstValue(
          typeof params.metadata === "string" ? params.metadata : undefined,
          params.tipoDePesquisa
        ) ?? "0";
      delete normalizedParams.empresaId;
      break;
    case EIntegracoesAPIEnum.PagHiper:
    case EIntegracoesAPIEnum.ApiAsaas:
      normalizedParams.metadata =
        pickFirstValue(
          typeof params.metadata === "string" ? params.metadata : undefined,
          params.tipoDePesquisa
        ) ?? "0";
      break;
    case EIntegracoesAPIEnum.ApiMarvin:
      normalizedParams.metadata =
        pickFirstValue(
          typeof params.metadata === "string" ? params.metadata : undefined,
          params.tipoDePesquisa
        ) ?? "0";
      break;
  }

  return normalizedParams;
};
